/** Minimal RGBA raster used by the imaging pipeline (compatible with the DOM ImageData layout). */
export interface Raster {
  width: number;
  height: number;
  /** RGBA, 4 bytes per pixel, row major. */
  data: Uint8ClampedArray;
}

export function createRaster(width: number, height: number): Raster {
  return { width, height, data: new Uint8ClampedArray(width * height * 4) };
}

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function crop(img: Raster, r: CropRect): Raster {
  const x0 = Math.max(0, Math.min(img.width - 1, Math.floor(r.x)));
  const y0 = Math.max(0, Math.min(img.height - 1, Math.floor(r.y)));
  const w = Math.max(1, Math.min(img.width - x0, Math.round(r.width)));
  const h = Math.max(1, Math.min(img.height - y0, Math.round(r.height)));
  const out = createRaster(w, h);
  for (let y = 0; y < h; y++) {
    const src = ((y0 + y) * img.width + x0) * 4;
    out.data.set(img.data.subarray(src, src + w * 4), y * w * 4);
  }
  return out;
}

/**
 * Downscales with area averaging (premultiplied alpha, so transparent pixels do not bleed
 * dark fringes into edges). Images already within `maxSide` are returned unchanged.
 */
export function downscale(img: Raster, maxSide: number): Raster {
  const s = Math.min(1, maxSide / Math.max(img.width, img.height));
  if (s >= 1) return img;
  const w = Math.max(1, Math.round(img.width * s));
  const h = Math.max(1, Math.round(img.height * s));
  const out = createRaster(w, h);
  const fx = img.width / w;
  const fy = img.height / h;
  for (let y = 0; y < h; y++) {
    const sy0 = Math.floor(y * fy), sy1 = Math.max(sy0 + 1, Math.floor((y + 1) * fy));
    for (let x = 0; x < w; x++) {
      const sx0 = Math.floor(x * fx), sx1 = Math.max(sx0 + 1, Math.floor((x + 1) * fx));
      let r = 0, g = 0, b = 0, a = 0, n = 0;
      for (let yy = sy0; yy < sy1 && yy < img.height; yy++) {
        for (let xx = sx0; xx < sx1 && xx < img.width; xx++) {
          const i = (yy * img.width + xx) * 4;
          const al = img.data[i + 3];
          r += img.data[i] * al;
          g += img.data[i + 1] * al;
          b += img.data[i + 2] * al;
          a += al;
          n++;
        }
      }
      const o = (y * w + x) * 4;
      if (a > 0) {
        out.data[o] = r / a;
        out.data[o + 1] = g / a;
        out.data[o + 2] = b / a;
      }
      out.data[o + 3] = n ? a / n : 0;
    }
  }
  return out;
}
